import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, UserPlus, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { ConfirmButton } from "@/components/ConfirmButton";
import { BatchItemsTable } from "@/components/batch/BatchItemsTable";
import { AddFromBatchDialog } from "@/components/batch/AddFromBatchDialog";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Stat = ({ label, value, testId }) => (
  <div className="panel px-4 py-3" data-testid={testId}>
    <p className="font-display text-2xl font-bold text-slate-900">{value}</p>
    <p className="text-xs text-slate-500">{label}</p>
  </div>
);

export default function BatchDetail() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { id } = useParams();
  const [batch, setBatch] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [period, setPeriod] = useState("");
  const [sending, setSending] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const apply = (data) => { setBatch(data); setPeriod(data.period || ""); };
  const load = useCallback(() => {
    api.get(`/batches/${id}`).then((r) => apply(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/employees", { params: { stato: "attivi" } }).then((r) => setEmployees(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const savePeriod = async () => {
    try {
      apply((await api.patch(`/batches/${id}`, { period })).data);
      toast.success("Periodo aggiornato");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const send = async (indexes) => {
    setSending(true);
    try {
      const { data } = await api.post(`/batches/${id}/send`, indexes ? { indexes } : {});
      apply(data.batch);
      if (data.failed) toast.error(`${data.sent} inviate, ${data.failed} non riuscite. ${data.errors[0] || ""}`, { duration: 15000 });
      else toast.success(`${data.sent} cedolini inviati correttamente`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  if (!batch) return <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const s = batch.summary;

  return (
    <>
      <Link to="/cedolini" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-uploads-link">
        <ArrowLeft className="h-4 w-4" /> Cedolini
      </Link>
      <PageHeader
        eyebrow={`${batch.filename} · ${batch.page_count} pagine`}
        title={batch.period || "Periodo non rilevato"}
        description="Controlla gli abbinamenti, poi invia. Solo le righe selezionate e abbinate verranno spedite."
        actions={
          <>
            {s.new_employees > 0 && (
              <Button variant="outline" onClick={() => setAddOpen(true)} data-testid="add-new-employees-button">
                <UserPlus className="mr-2 h-4 w-4" /> Aggiungi {s.new_employees} nuovi dipendenti
              </Button>
            )}
            {!isAdmin && <span className="self-center text-xs text-slate-500" data-testid="send-admin-only-hint">L'invio dei cedolini è riservato all'amministratore</span>}
            {isAdmin && <ConfirmButton
              variant="default" size="default" testId="send-all-payslips-button" disabled={sending || s.to_send === 0}
              className="active:scale-[0.98]" confirmLabel="Invia ora"
              title={`Inviare ${s.to_send} cedolini? Controlla destinatari e periodo: le buste già inviate non vengono rispedite.`}
              description={`Periodo: ${batch.period || "non indicato"}. Verranno spediti solo i cedolini selezionati e abbinati, ciascuno al proprio dipendente; quelli già inviati restano esclusi. Controlla la tabella: destinatario, email e pagine.`}
              onConfirm={() => send()}
            >
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Invia {s.to_send} cedolini
            </ConfirmButton>}
          </>
        }
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Cedolini trovati" value={s.total} testId="stat-total" />
        <Stat label="Abbinate" value={s.matched} testId="stat-matched" />
        <Stat label="Inviate" value={s.sent} testId="stat-sent" />
        <Stat label="Da verificare" value={s.issues} testId="stat-issues" />
      </div>
      <div className="panel flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <span className="eyebrow shrink-0">Periodo (usato in oggetto e nome file)</span>
        <Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="es. Settembre 2026" className="sm:max-w-xs" data-testid="batch-period-input" />
        <Button variant="outline" size="sm" onClick={savePeriod} disabled={period === (batch.period || "")} data-testid="batch-period-save-button">
          <Save className="mr-2 h-4 w-4" /> Salva
        </Button>
      </div>
      <BatchItemsTable batch={batch} employees={employees} onChange={apply} onSendOne={isAdmin ? (i) => send([i]) : null} sending={sending} />
      <AddFromBatchDialog open={addOpen} onOpenChange={setAddOpen} batch={batch} onDone={(data) => { apply(data); load(); }} />
    </>
  );
}
